import React, {
	type ComponentType,
	type ErrorInfo,
	type ReactNode,
	type Ref,
	type RefObject,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from 'react';

import { loadEntryPoint, type EntryPointProps as RelayEntryPointProps } from 'react-relay';
import { useMergeRefs } from 'use-callback-ref';

import {
	FullScreenModalDialog,
	type FullScreenModalDialogProps,
} from '@atlaskit/modal-dialog/full-screen';
import { useEntryPointConfig } from '@atlassian/entry-point-config/use-entry-point-config';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
} from '@atlassian/entry-point-types/types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container/ui';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { ModalContext } from '../modal-context-provider/modal-context';
import { type ModalComponentProps } from '../modal-context-provider/modal-context-provider';
import { ErrorFallback } from './error-fallback';
import { FocusInsideOnLoad } from './focus-inside-on-load';
import { Loading } from './loading';
import { type SourceMetadata } from './types';

export type ModalEntryPointProps<
	TPreloadedQueries,
	TNestedEntryPoints,
	TRuntimeProps,
	TExtraProps,
> = RelayEntryPointProps<
	TPreloadedQueries,
	TNestedEntryPoints,
	TRuntimeProps & ModalComponentProps,
	TExtraProps
>;

type EntryPointProps<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

export type FullScreenModalTriggerProps<TEntryPoint> = {
	children: ({ ref }: { ref: RefObject<HTMLElement>; 'aria-haspopup'?: 'dialog' }) => ReactNode;
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<
		EntryPointProps<TEntryPoint>,
		| 'onClose'
		| 'setShouldCloseOnOverlayClick'
		| 'setShouldCloseOnEscapePress'
		| 'shouldCloseOnOverlayClick'
		| 'shouldCloseOnEscapePress'
	> & {
		onClose?: (extraInformation?: unknown) => void;
	};
	Fallback?: ComponentType<{ onClose?: () => void }>;
	forwardedRef?: Ref<HTMLElement>;
	modalProps?: FullScreenModalDialogProps;
	onError?: ErrorCallback;
	source?: SourceMetadata;
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	preloadOnFocus?: boolean;
	/**
	 * The title that renders during the default loading screen and the default error screen when
	 * `errorTitle` is not provided.
	 */
	title?: ReactNode;
	/**
	 * The text that renders during the loading screen.
	 */
	loadingText?: ReactNode;
	/**
	 * The title that renders during the default error screen. Falls back to `title` when omitted.
	 */
	errorTitle?: ReactNode;
	/**
	 * The text that renders during the default error screen.
	 */
	errorText?: ReactNode;
	/**
	 * The text used by the close button on the default error screen.
	 */
	errorCloseText?: string;
};

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as EntryPointProps<AnyEntryPoint>;

export function FullScreenModalTrigger<TEntryPoint extends AnyEntryPoint>({
	children,
	entryPoint,
	entryPointParams,
	entryPointProps,
	Fallback,
	title,
	modalProps,
	forwardedRef,
	onError,
	source,
	preloadOnFocus = true,
	loadingText,
	errorTitle,
	errorText,
	errorCloseText,
}: FullScreenModalTriggerProps<TEntryPoint>): React.ReactNode {
	const { openModal } = useContext(ModalContext);
	const environmentProvider = useRelayEnvironmentProvider();
	const { GlobalModalErrorFallback } = useEntryPointConfig();
	const modalRef = useRef<HTMLElement>(null);
	const { id, packageName, teamName } = source ?? {};

	const metricName = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<unknown>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({
			reference: entryPointReference,
			dispose,
		}: {
			dispose: () => void;
			reference: EntryPointRef<TEntryPoint>;
		}) => {
			openModal(
				({
					onClose: closeModal,
					shouldCloseOnOverlayClick,
					shouldCloseOnEscapePress,
					setShouldCloseOnOverlayClick,
					setShouldCloseOnEscapePress,
				}) => {
					const onClose = (extraInformation?: unknown) => {
						entryPointProps?.onClose?.(extraInformation);
						closeModal();
						dispose();
					};

					const container = (
						<InternalEntryPointContainer
							entryPointReference={entryPointReference}
							errorFallback={({ error }: { error: Error }) => (
								<GlobalModalErrorFallback
									error={error}
									id={id ?? metricName}
									teamName={teamName}
									packageName={packageName}
									closeModal={onClose}
									fallback={
										<ErrorFallback
											error={error}
											closeModal={onClose}
											errorTitle={errorTitle}
											title={title}
											errorText={errorText}
											errorCloseText={errorCloseText}
										/>
									}
								/>
							)}
							onError={({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo }) => {
								onError?.({ error, errorInfo });
							}}
							fallback={
								Fallback ? (
									<Fallback onClose={closeModal} />
								) : (
									<Loading title={title} loadingText={loadingText} />
								)
							}
							id={metricName}
							placeholderName="modal-entry-point-pressable-trigger-container"
							runtimeProps={
								{
									...(entryPointProps ?? emptyEntryPointProps),
									onClose,
									setShouldCloseOnEscapePress,
									setShouldCloseOnOverlayClick,
									shouldCloseOnEscapePress,
									shouldCloseOnOverlayClick,
								} as unknown as EntryPointProps<TEntryPoint>
							}
						/>
					);

					return (
						<FullScreenModalDialog
							{...modalProps}
							onClose={onClose}
							ref={modalRef}
							shouldCloseOnEscapePress={
								shouldCloseOnEscapePress ?? modalProps?.shouldCloseOnEscapePress
							}
						>
							{container}
							<FocusInsideOnLoad
								entryPointReference={entryPointReference}
								autoFocusRef={modalProps?.autoFocus}
								modalRef={modalRef}
							/>
						</FullScreenModalDialog>
					);
				},
			);
		},
		[
			openModal,
			title,
			modalProps,
			Fallback,
			metricName,
			id,
			packageName,
			teamName,
			entryPointProps,
			GlobalModalErrorFallback,
			onError,
			loadingText,
			errorTitle,
			errorText,
			errorCloseText,
		],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		preloadOnFocus,
	});

	const mergedRef = useMergeRefs([triggerRef, forwardedRef || null]);

	return children({
		ref: mergedRef as unknown as RefObject<HTMLElement>,
		'aria-haspopup': 'dialog',
	});
}
